// app 1.5.42 — the M® card on M's side: the enrolled credential, the rule that asks for it, the pending payment across
// the approver's round trip, and the stand-in verifier. The protocol: cardApproval.ts; the design: claude/multisig-card.md.

import { verify } from "webauthn-p256";
import { touch } from "./settings"; // 1.5.50 — the card record and the limit sync with the wallet
import type { Hex } from "viem";
import {
  approvalChallenge, decodeFragment, encodeFragment, opHash, readTransfers,
  type Approval, type CardCredential, type EnrolRequest, type Handoff, type HandoffOp,
} from "./cardApproval";

const CARD_KEY = "m.card";
const PENDING_KEY = "m.cardPending";
const ABOVE_KEY = "m.cardAbove";

/** The approver's origin: the Pages project of its own (claude/multisig-card.md §9a). The harness points it at a
 *  second local origin — approver.localhost — whose rpId differs from localhost's, so the scoping is real there too. */
export function approverOrigin(): string {
  return (import.meta.env.VITE_APPROVER_URL as string | undefined)?.replace(/\/$/, "") || "https://approve.globalmoneyapp.com";
}

export function readCard(): CardCredential | null {
  try {
    const raw = localStorage.getItem(CARD_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as CardCredential;
    return c && c.id && c.publicKey && c.rpId ? c : null;
  } catch { return null; }
}
export function saveCard(c: CardCredential): void {
  try { localStorage.setItem(CARD_KEY, JSON.stringify(c)); touch("card"); } catch { /* storage refused */ }
}
/** 1.5.50 — removal is a tombstone (""), stamped now, so the wallet's synced copy cannot bring the card back. */
export function clearCard(): void {
  try { localStorage.setItem(CARD_KEY, ""); touch("card"); } catch { /* ignore */ }
}
/** 1.5.50 — the limit is the wallet's too. */
export function setCardAbove(v: number): void {
  try { localStorage.setItem(ABOVE_KEY, String(v)); touch("cardAbove"); } catch { /* ignore */ }
}

/** $X — above it the card is asked for. A device setting until the rule lives on the account (where a spiked app
 *  cannot raise it); 2 in the payment's currency by default (1.5.43, Alexander, 30 Oct: "I suggest $2" — so a coffee at
 *  Ondas asks for the card in the demo). 0 asks for the card on every payment. */
export function cardAbove(): number {
  try { const v = localStorage.getItem(ABOVE_KEY); if (v !== null && v !== "" && Number.isFinite(Number(v))) return Number(v); } catch { /* ignore */ }
  return 2;
}

/** Does THIS payment need the card? The wallet must have one enrolled, and the amount be above $X. */
export function needsCard(address: string | null | undefined, amount: number): boolean {
  const c = readCard();
  if (!c || !address || c.address.toLowerCase() !== address.toLowerCase()) return false;
  return amount > cardAbove();
}
/** 1.5.51 — a wallet's card record and limit read straight from its synced settings (settings.ts' bag, the `card` and
 *  `cardAbove` keys), for a wallet that is NOT this browser's bound one — a pick in the frame or on the pay page — whose
 *  settings must never be merged into this browser's (they are another wallet's theme, pockets and card). */
export function cardFromSettings(settings: string | null | undefined, address: string): { card: CardCredential; above: number } | null {
  if (!settings) return null;
  try {
    const bag = JSON.parse(settings) as Record<string, { v?: unknown }>;
    const raw = bag.card?.v; if (typeof raw !== "string" || !raw) return null;
    const c = JSON.parse(raw) as CardCredential;
    if (!c || !c.id || !c.publicKey || !c.rpId || typeof c.address !== "string" || c.address.toLowerCase() !== address.toLowerCase()) return null;
    const a = bag.cardAbove?.v; const above = typeof a === "string" && a !== "" && Number.isFinite(Number(a)) ? Number(a) : 2;
    return { card: c, above };
  } catch { return null; }
}

// ---- the pending payment (M's tab leaves and comes back) ------------------------------------------------------

export interface PendingPay {
  v: 1;
  /** the page to resume: its own query string, as it was */
  href: string;
  at: number;
  address: Hex;
  chainId: number;
  entryPoint: Hex;
  op: HandoffOp;
  hash: Hex;
  amount: string;
  currency?: string;
  toAddress: string;
  /** 1.5.47 — the passkey's signature over the op, made on the Pay tap before the trip (Apple Pay's order) */
  signature?: Hex;
  /** 1.5.51 — the wallet that signed, when it is not this browser's bound one ("Use another M® wallet", or a checkout's
   *  hand-off for a wallet picked in the frame): its public values, so the return connects it with no prompt */
  cred?: { id: string; publicKey: string; rpId?: string; address: string };
  /** 1.5.51 — that wallet's card record (not this browser's), to verify the approver's answer against */
  card?: CardCredential;
}

export function savePending(p: PendingPay): void {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
export function readPending(): PendingPay | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingPay;
    return p && p.v === 1 && p.hash ? p : null;
  } catch { return null; }
}
export function clearPending(): void {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

// ---- the round trip --------------------------------------------------------------------------------------------

/** Leave for the approver with the sponsored op. The return address is this page without its fragment. */
export function goToApprover(h: Omit<Handoff, "v" | "ret">): void {
  const ret = location.href.replace(/#.*$/, "");
  const handoff: Handoff = { v: 1, ret, ...h };
  location.assign(approverOrigin() + "/" + encodeFragment("a", handoff));
}

/** Leave for the approver to make the card's credential. 1.5.47 — the trip is remembered for the tab (m.cardTrip),
 *  so the home reopens for the same wallet on the way back without a second Face ID (within the idle logout's 3 min). */
const TRIP_KEY = "m.cardTrip";
export function goToEnrol(address: Hex, name: string, ret = location.href.replace(/#.*$/, "")): void {
  const req: EnrolRequest = { v: 1, ret, address, name };
  try { sessionStorage.setItem(TRIP_KEY, JSON.stringify({ address, at: Date.now() })); } catch { /* ignore */ }
  location.assign(approverOrigin() + "/" + encodeFragment("e", req));
}
/** The wallet that left for the approver's enrolment from this tab less than 3 minutes ago, consumed once. */
export function takeTrip(): Hex | null {
  try {
    const raw = sessionStorage.getItem(TRIP_KEY);
    sessionStorage.removeItem(TRIP_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as { address: Hex; at: number };
    return t && t.address && Date.now() - t.at < 3 * 60_000 ? t.address : null;
  } catch { return null; }
}

/** What the approver brought back in the fragment (and clear it from the address bar). */
export function takeReturn(): { approval: Approval } | { refused: string } | { card: CardCredential } | null {
  const h = location.hash;
  if (!h || h.length < 3) return null;
  let out: ReturnType<typeof takeReturn> = null;
  const a = decodeFragment<Approval>(h, "c");
  if (a && a.v === 1 && a.hash && a.signature) out = { approval: a };
  const k = decodeFragment<CardCredential>(h, "k");
  if (k && k.v === 1 && k.id && k.publicKey) out = { card: k };
  const x = /^#x=([A-Za-z0-9_-]{1,40})$/.exec(h);
  if (x) out = { refused: x[1] };
  if (out) history.replaceState(null, "", location.href.replace(/#.*$/, ""));
  return out;
}

/** The stand-in verifier (until the account's module on Arc does this where the money moves): the card's assertion
 *  is over sha256(fields ‖ hash); the fields are recomputed from the op's calldata, the hash from the op — so the
 *  figures the card signed cannot differ from what executes. Returns the reason it fails, or null. */
export async function verifyApproval(p: PendingPay, a: Approval, card: CardCredential): Promise<string | null> {
  if (a.cred !== card.id) return "card_other_credential";
  if (a.hash.toLowerCase() !== p.hash.toLowerCase()) return "card_other_payment";
  const hash = opHash({ chainId: p.chainId, entryPoint: p.entryPoint, op: p.op });
  if (hash.toLowerCase() !== p.hash.toLowerCase()) return "card_hash_mismatch";
  let fields;
  try { fields = readTransfers(p.op, p.chainId); } catch { return "card_unreadable"; }
  const challenge = approvalChallenge(fields, hash);
  if (approvalChallenge(a.fields, hash) !== challenge) return "card_fields_differ"; // the approver showed other figures
  let ok = false;
  try {
    ok = await verify({ hash: challenge, publicKey: card.publicKey, signature: a.signature, webauthn: a.webauthn });
  } catch { ok = false; }
  return ok ? null : "card_bad_signature";
}
